export type VideoQualityTier = 'low' | 'medium' | 'high' | 'ultra';

export type VideoQualityPreference = VideoQualityTier | 'auto';

/** @deprecated VideoQualityTier kullanın */
export type VideoQuality = VideoQualityTier;

export const VIDEO_QUALITY_ORDER: VideoQualityTier[] = ['low', 'medium', 'high', 'ultra'];

/** Kamera ve WebRTC için hedef kare hızı; üst sınır konmaz, cihazın verebildiği en iyi değer kullanılır. */
export const PREFERRED_FRAMERATE = 30;

export const VIDEO_QUALITY_OPTIONS: Array<{
  value: VideoQualityPreference;
  label: string;
  detail: string;
}> = [
  { value: 'auto', label: 'Otomatik', detail: 'Ağa göre ayarlanır' },
  { value: 'low', label: 'Düşük', detail: '360p' },
  { value: 'medium', label: 'Orta', detail: '720p' },
  { value: 'high', label: 'Yüksek', detail: '1080p' },
  { value: 'ultra', label: 'Ultra', detail: '1440p' },
];

export const VIDEO_QUALITY_CONSTRAINTS: Record<
  VideoQualityTier,
  { width: number; height: number }
> = {
  low: { width: 640, height: 360 },
  medium: { width: 1280, height: 720 },
  high: { width: 1920, height: 1080 },
  ultra: { width: 2560, height: 1440 },
};

export const VIDEO_SENDER_PROFILES: Record<
  VideoQualityTier,
  { maxBitrate: number; maxFramerate: number; scaleResolutionDownBy: number }
> = {
  low: { maxBitrate: 400_000, maxFramerate: PREFERRED_FRAMERATE, scaleResolutionDownBy: 2 },
  medium: { maxBitrate: 1_200_000, maxFramerate: PREFERRED_FRAMERATE, scaleResolutionDownBy: 1.25 },
  high: { maxBitrate: 2_500_000, maxFramerate: PREFERRED_FRAMERATE, scaleResolutionDownBy: 1 },
  ultra: { maxBitrate: 4_500_000, maxFramerate: PREFERRED_FRAMERATE, scaleResolutionDownBy: 1 },
};

export function getVideoConstraints(
  quality: VideoQualityTier,
  deviceId?: string
): MediaTrackConstraints {
  const cfg = VIDEO_QUALITY_CONSTRAINTS[quality];
  return {
    width: { ideal: cfg.width, max: cfg.width },
    height: { ideal: cfg.height, max: cfg.height },
    frameRate: { ideal: PREFERRED_FRAMERATE },
    aspectRatio: { ideal: 16 / 9 },
    ...(deviceId ? { deviceId: { exact: deviceId } } : { facingMode: 'user' }),
  };
}

export function getQualityLabel(tier: VideoQualityTier): string {
  return VIDEO_QUALITY_OPTIONS.find((o) => o.value === tier)?.label ?? tier;
}

export interface WebRTCNetworkMetrics {
  peerCount: number;
  packetLossPercent: number;
  roundTripTimeMs: number;
  outboundFps: number;
  outboundBitrateKbps: number;
  bandwidthLimited: boolean;
}

export function getMaxTierFromConnection(): VideoQualityTier {
  const conn = (navigator as Navigator & {
    connection?: { effectiveType?: string };
  }).connection;

  const effectiveType = conn?.effectiveType;
  if (effectiveType === 'slow-2g' || effectiveType === '2g') return 'low';
  if (effectiveType === '3g') return 'medium';
  return 'ultra';
}

const DOWNGRADE_STREAK = 2;
const UPGRADE_STREAK = 3;

export function decideAdaptiveQuality(
  current: VideoQualityTier,
  metrics: WebRTCNetworkMetrics,
  badStreak: number,
  goodStreak: number
): { next: VideoQualityTier; badStreak: number; goodStreak: number } {
  const maxTier = getMaxTierFromConnection();
  let bad = badStreak;
  let good = goodStreak;

  const isBad =
    metrics.packetLossPercent > 3 ||
    metrics.roundTripTimeMs > 350 ||
    metrics.outboundFps < 12 ||
    metrics.bandwidthLimited;

  const isGood =
    metrics.packetLossPercent < 1 &&
    metrics.roundTripTimeMs > 0 &&
    metrics.roundTripTimeMs < 180 &&
    metrics.outboundFps >= 20 &&
    !metrics.bandwidthLimited;

  if (isBad) {
    bad += 1;
    good = 0;
    if (bad >= DOWNGRADE_STREAK) {
      const idx = VIDEO_QUALITY_ORDER.indexOf(current);
      if (idx > 0) {
        return { next: VIDEO_QUALITY_ORDER[idx - 1], badStreak: 0, goodStreak: 0 };
      }
      return { next: current, badStreak: 0, goodStreak: 0 };
    }
  } else if (isGood) {
    good += 1;
    bad = 0;
    if (good >= UPGRADE_STREAK) {
      const idx = VIDEO_QUALITY_ORDER.indexOf(current);
      const maxIdx = VIDEO_QUALITY_ORDER.indexOf(maxTier);
      if (idx < maxIdx) {
        return { next: VIDEO_QUALITY_ORDER[idx + 1], badStreak: 0, goodStreak: 0 };
      }
      return { next: current, badStreak: 0, goodStreak: 0 };
    }
  } else {
    bad = Math.max(0, bad - 1);
    good = Math.max(0, good - 1);
  }

  return { next: current, badStreak: bad, goodStreak: good };
}
